import { useAiTagPreviewApi } from '@/common/api/modules/aiTag'
import { AITagMaster } from '@/common/model/recommend/aiTag'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

interface Props {
  tag: AITagMaster | null
  siteId: number
  onClose: () => void
}

const AITagPreviewModal = ({ tag, siteId, onClose }: Props) => {
  const { data, isLoading } = useAiTagPreviewApi(tag ? { site_id: siteId, tag_id: tag.id } : null)

  return (
    <Modal show={!!tag} type={ModalType.LARGE}>
      <ModalHeader
        text={tag ? `紐づけ詳細: ${tag.tag_name}` : '紐づけ詳細'}
        onClickClose={onClose}
      />
      {tag && (
        <>
          <div className='border-secondary flex items-center gap-3 border-b px-4 py-3'>
            {data && (
              <Text size={12} color={TextColor.SECONDARY}>
                紐づく商品: {data.total}件
              </Text>
            )}
          </div>
          <ModalContent type={ModalContentType.FLEX_GROW}>
            <div className='px-2 pb-2'>
              {isLoading ? (
                <div className='py-10 text-center'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    読み込み中...
                  </Text>
                </div>
              ) : !data ? null : (
                <>
                  {data.related_tags && data.related_tags.length > 0 && (
                    <div className='mb-5'>
                      <div className='mb-2'>
                        <Text size={12} color={TextColor.SECONDARY}>
                          同カテゴリの関連タグ
                        </Text>
                      </div>
                      <div className='flex flex-wrap gap-1.5'>
                        {data.related_tags.map((rt) => (
                          <div key={rt.id} className='flex items-center gap-0.5'>
                            <Text>{rt.name ?? tag.tag_category}</Text>
                            <Text size={10} color={TextColor.SECONDARY}>
                              {rt.score.toFixed(2)}
                            </Text>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {data.products.length === 0 ? (
                    <div className='py-10 text-center'>
                      <Text size={12} color={TextColor.SECONDARY}>
                        紐づく商品がありません
                      </Text>
                    </div>
                  ) : (
                    <div className='grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3'>
                      {data.products.map((p) => (
                        <div
                          key={p.id}
                          className='border-secondary bg-fill-surface-secondary overflow-hidden rounded-xl border'
                        >
                          {p.image_url ? (
                            <div className='bg-fill-tertiary aspect-square w-full overflow-hidden'>
                              <img
                                src={p.image_url}
                                alt={p.name}
                                className='h-full w-full object-cover'
                                onError={(e) => {
                                  ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                                }}
                              />
                            </div>
                          ) : (
                            <div className='bg-fill-tertiary flex aspect-square w-full items-center justify-center'>
                              <Text size={24} color={TextColor.SECONDARY}>
                                📷
                              </Text>
                            </div>
                          )}
                          <div className='p-3'>
                            <div className='mb-1'>
                              <Text size={12} weight='bold'>
                                {p.name || '(名称なし)'}
                              </Text>
                            </div>
                            {p.price != null && (
                              <div className='mb-1'>
                                <Text size={12} weight='bold' color={TextColor.CAUTION}>
                                  ¥{Number(p.price).toLocaleString()}
                                </Text>
                              </div>
                            )}
                            {p.category && (
                              <Text size={10} color={TextColor.SECONDARY}>
                                {p.category}
                              </Text>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </ModalContent>
        </>
      )}
      <ModalFooter>
        <Button color={ButtonColor.SECONDARY} size='large' onClick={onClose}>
          閉じる
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default AITagPreviewModal
